import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { BASE_API } from './constants';
import styles from './PatientWorkflowPage.module.css';
import Zoom from 'react-medium-image-zoom';
import 'react-medium-image-zoom/dist/styles.css';
import AdobePdfIcon from './AdobePdfIcon';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import medicalData from './data/medicalData.json';
import { MedicalData } from './types/medicalData';
import aiSummaryData from './data/aiSummaryData.json';
import { AISummaryData } from './types/aiSummaryData';
import Lottie from 'lottie-react';
import docScannerAnimation from './Doc scanner.lottie';

// Define allowed step statuses
type StepStatus = 'completed' | 'pending' | 'failed';

interface WorkflowStep {
  id: string;
  title: string;
  description: string;
  stepOrder: number;
  actionType: string;
  stepStatus: StepStatus;
  actionStatus?: any;
  actionData?: any;
  updatedAt?: string;
}

const statusColors: Record<StepStatus, string> = {
  completed: '#43cea2',
  pending: '#ffd200',
  failed: '#d32f2f',
};

interface Patient {
  id: string;
  demographics: {
    fullName?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    age?: string;
    pictureUrl?: string;
    nickname?: string;
    gender?: string;
  };
  patientLastActiveTime?: string;
}

interface CalendarSlot {
  scheduleId: string;
  startTime: string;
  slot: number;
  timeBlock: number;
  status: 'free' | 'busy';
  appointmentId?: string;
  patient?: {
    id: string;
    demographics: {
      fullName?: string;
      firstName?: string;
      lastName?: string;
      email?: string;
      nickname?: string;
      pictureUrl?: string;
    };
  };
}

interface CalendarDay {
  day: string;
  slots: CalendarSlot[];
}

interface FileObject {
  fileName: string;
  url: string;
}

interface PatientWorkflowPageProps {
  patient: Patient;
  nurseCalendar: CalendarDay[];
}

const TABS = [
  { label: 'Medical History', key: 'summary' },
  { label: 'Telehealth Sessions', key: 'visits' },
  { label: 'Health Records and Data', key: 'documents' },
  { label: 'AI Summaries and Notes', key: 'physicianFeedback' },
  // { label: 'Other', key: 'other' },
];

const getNurseId = (): string | null => {
  // Try to get nurseId from localStorage (or pass as prop if you prefer)
  return localStorage.getItem('nurseId');
};

const getAccessToken = (): string | null => {
  return localStorage.getItem('id_token') || localStorage.getItem('access_token');
};

// Helper to get file extension
const getFileExtension = (fileName: string) => {
  const match = fileName.match(/\.([a-zA-Z0-9]+)$/);
  return match ? match[1].toLowerCase() : '';
};

// Helper to format date into a relative time string
const formatDate = (dateString: string | undefined) => {
  if (!dateString) return '';

  const date = new Date(dateString);
  const now = new Date();
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (seconds < 60) {
    return `${seconds} sec${seconds === 1 ? '' : 's'} ago`;
  }

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    return `${minutes} min${minutes === 1 ? '' : 's'} ago`;
  }

  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  }

  const days = Math.floor(hours / 24);
  if (days < 7) {
    return `${days} day${days === 1 ? '' : 's'} ago`;
  }

  // For longer periods, return a more standard date format
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

// Helper to mask email address
const maskEmail = (email: string) => {
  if (!email) return '';
  const [localPart, domain] = email.split('@');
  if (!domain) return email;
  
  const maskedLocal = localPart.length > 2 
    ? localPart.charAt(0) + '*'.repeat(localPart.length - 2) + localPart.charAt(localPart.length - 1)
    : localPart;
  
  const [domainName, extension] = domain.split('.');
  const maskedDomain = domainName.length > 2
    ? domainName.charAt(0) + '*'.repeat(domainName.length - 2) + domainName.charAt(domainName.length - 1)
    : domainName;
  
  return `${maskedLocal}@${maskedDomain}.${extension}`;
};

const PatientWorkflowPage: React.FC<PatientWorkflowPageProps> = ({ patient, nurseCalendar = [] }) => {
  const navigate = useNavigate();
  const [workflow, setWorkflow] = useState<WorkflowStep[]>([]);
  const [tab, setTab] = useState('summary');
  const [documents, setDocuments] = useState<FileObject[] | null>(null);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [docError, setDocError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const previewRef = useRef<HTMLDialogElement>(null);
  const [feedback, setFeedback] = useState('');
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [feedbackError, setFeedbackError] = useState<string | null>(null);
  const [submittingFeedback, setSubmittingFeedback] = useState(false);
  const [editingFeedback, setEditingFeedback] = useState(false);
  const [docScannerData, setDocScannerData] = useState<any>(null);
  const [emailRevealed, setEmailRevealed] = useState(false);

  // Find the fetch step that contains medical data
  const fetchStep = workflow.find(
    step => step.actionType === 'fetch' && step.stepStatus === 'completed'
  );

  // Find the report step
  const reportStep = workflow.find(
    step => step.actionType === 'report' && step.stepStatus === 'completed'
  );

  // Get medical data from fetch step actionData or return null if not available
  const getMedicalData = (): MedicalData | null => {
    if (fetchStep && fetchStep.actionData) {
      // Validate that actionData has the expected structure
      if (fetchStep.actionData.medicalConditions && fetchStep.actionData.medications) {
        console.log('Using medical data from fetch step:', fetchStep.actionData);
        return fetchStep.actionData as MedicalData;
      }
    }
    // No valid data available
    console.log('No valid medical data found from fetch step');
    return null;
  };

  // Get AI summary data from report step actionData or return static data
  const getAISummaryData = (): AISummaryData => {
    if (reportStep && reportStep.actionData) {
      // Validate that actionData has the expected structure
      if (reportStep.actionData.summary && reportStep.actionData.keyInsights) {
        console.log('Using AI summary data from report step:', reportStep.actionData);
        
        // Transform potentialAnomalies if it's an array of strings (backend format)
        let transformedAnomalies = reportStep.actionData.potentialAnomalies;
        if (Array.isArray(reportStep.actionData.potentialAnomalies) && 
            reportStep.actionData.potentialAnomalies.length > 0 && 
            typeof reportStep.actionData.potentialAnomalies[0] === 'string') {
          // Transform string array to object array format
          transformedAnomalies = reportStep.actionData.potentialAnomalies.map((message: string) => ({
            type: 'warning' as const,
            message: message,
            icon: 'warning'
          }));
        }
        
        return {
          summary: reportStep.actionData.summary,
          keyInsights: reportStep.actionData.keyInsights,
          potentialAnomalies: transformedAnomalies || []
        } as AISummaryData;
      }
    }
    // Fall back to static data
    console.log('Using static AI summary data from JSON file');
    return aiSummaryData as AISummaryData;
  };

  const medicalDataForPatient = getMedicalData();
  const aiSummaryDataForPatient = getAISummaryData();

  // Debug: Log the medical data structure
  console.log('medicalDataForPatient:', medicalDataForPatient);

  const comprehensiveStep = workflow.find(
    step => step.title && step.title.toLowerCase().includes('comprehensive report')
  );
  const feedbackExists = comprehensiveStep && comprehensiveStep.actionData && typeof comprehensiveStep.actionData.feedback === 'string' && comprehensiveStep.actionData.feedback.trim() !== '';
  const canSubmit = feedback.trim() !== '' && !submittingFeedback && (!feedbackExists || editingFeedback);

  const handleSave = async () => {
    const nurseId = getNurseId();
    const token = getAccessToken();
    if (!nurseId || !token || !comprehensiveStep || !canSubmit) return;

    setSubmittingFeedback(true);
    setFeedbackError(null);
    try {
      const res = await fetch(`${BASE_API}/nurses/${nurseId}/patients/${patient.id}/journey-steps/${comprehensiveStep.id}/complete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-access-token': token,
        },
        body: JSON.stringify({ 
          ...((comprehensiveStep.actionData) || {}),
          feedback
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to submit feedback');
      }
      // Update the workflow state with the new feedback and timestamp
      setWorkflow(prevWorkflow =>
        prevWorkflow.map(step =>
          step.id === comprehensiveStep?.id
            ? { ...step, actionData: { ...(step.actionData || {}), feedback }, updatedAt: new Date().toISOString() }
            : step
        )
      );
      setFeedbackSubmitted(true);
      setEditingFeedback(false);
    } catch (err: any) {
      setFeedbackError(err.message || 'Failed to submit feedback');
    } finally {
      setSubmittingFeedback(false);
    }
  };

  useEffect(() => {
    // Fetch workflow for patient id from API
    const nurseId = getNurseId();
    const token = getAccessToken();
    if (!nurseId || !token) return;
    fetch(`${BASE_API}/nurses/${nurseId}/patients/${patient.id}/journey-steps`, {
      headers: {
        'x-access-token': token,
      },
    })
      .then(res => res.json())
      .then((data: WorkflowStep[]) => {
        setWorkflow(Array.isArray(data) ? data : []);
      })
      .catch(() => setWorkflow([]));
  }, [patient.id]);

  // Load feedback from actionData if it exists when workflow changes
  useEffect(() => {
    const comprehensiveStep = workflow.find(
      step => step.title && step.title.toLowerCase().includes('comprehensive report')
    );
    if (comprehensiveStep && comprehensiveStep.actionData && typeof comprehensiveStep.actionData === 'object') {
      if (typeof comprehensiveStep.actionData.feedback === 'string') {
        setFeedback(comprehensiveStep.actionData.feedback);
        setFeedbackSubmitted(true);
        setEditingFeedback(false);
      } else {
        setFeedback('');
        setFeedbackSubmitted(false);
        setEditingFeedback(false);
      }
    } else {
      setFeedback('');
      setFeedbackSubmitted(false);
      setEditingFeedback(false);
    }
  }, [workflow]);

  // Fetch documents when Documents tab is selected
  useEffect(() => {
    if (tab === 'documents') {
      const nurseId = getNurseId();
      const token = getAccessToken();
      if (!nurseId || !token) return;
      setLoadingDocs(true);
      setDocError(null);
      fetch(`${BASE_API}/nurses/${nurseId}/patients/${patient.id}/medical-records`, {
        headers: {
          'x-access-token': token,
        },
      })
        .then(res => res.json())
        .then((data: FileObject[]) => {
          setDocuments(Array.isArray(data) ? data : []);
        })
        .catch(() => setDocError('Failed to load documents.'))
        .finally(() => setLoadingDocs(false));
    }
  }, [tab, patient.id]);

  // Reset documents state when tab changes (so API is called every time)
  useEffect(() => {
    if (tab !== 'documents') {
      setDocuments(null);
      setDocError(null);
      setLoadingDocs(false);
    }
  }, [tab, patient.id]);

  // Load Lottie animation data
  useEffect(() => {
    fetch('/DocScanner.json')
      .then(response => response.json())
      .then(data => setDocScannerData(data))
      .catch(error => {
        console.error('Error loading Lottie animation:', error);
        setDocScannerData(null);
      });
  }, []);

  // Horizontal stepper for workflow steps
  const renderStepper = () => (
    <div className={styles.stepper}>
      {workflow.map((step, idx) => {
        // Determine which tab to go to on click
        let onStepClick: (() => void) | undefined;
        if (step.title && step.title.toLowerCase().includes('comprehensive report')) {
          onStepClick = () => setTab('physicianFeedback');
        } else if (step.title && step.title.toLowerCase().includes('view medical records')) {
          onStepClick = () => setTab('summary');
        } else if (step.title && step.title.toLowerCase().includes('telehealth session')) {
          onStepClick = () => setTab('visits');
        }
        return (
          <React.Fragment key={step.id}>
            <div
              className={
                `${styles.step} ${step.stepStatus === 'completed' ? styles.completed : ''} ${step.stepStatus === 'pending' ? styles.pending : ''}` +
                (onStepClick ? ' ' + styles.stepClickable : '')
              }
              onClick={onStepClick}
              style={onStepClick ? { cursor: 'pointer' } : {}}
              title={onStepClick ? 'Click to view related tab' : undefined}
            >
              <div className={styles.stepCircleWrapper}>
                <div
                  className={
                    styles.stepCircle + ' ' +
                    (step.stepStatus === 'completed'
                      ? styles.stepCircleCompleted
                      : step.stepStatus === 'pending'
                      ? styles.stepCirclePending
                      : styles.stepCircleDefault)
                  }
                >
                  {idx + 1}
                </div>
              </div>
              <div className={styles.stepText}>
                <div className={styles.stepTitle}>{step.title}</div>
                <div className={styles.stepStatus}>{step.stepStatus}</div>
              </div>
            </div>
            {idx < workflow.length - 1 && <div className={styles.stepConnector} />}
          </React.Fragment>
        );
      })}
    </div>
  );

  // Tab view
  const renderTabs = () => (
    <div className={styles.tabs}>
      {TABS.map(t => (
        <button
          key={t.key}
          onClick={() => setTab(t.key)}
          className={
            tab === t.key
              ? `${styles.tabButton} ${styles.tabButtonActive}`
              : styles.tabButton
          }
        >
          {t.label}
        </button>
      ))}
    </div>
  );

  // Tab content (placeholder for now)
  const renderTabContent = () => {
    switch (tab) {
      case 'summary':
        return (
          <div className={styles.tabContent}>
            {/* Medical Conditions Section */}
            <div className={styles.medicalSection}>
              <h2 className={styles.sectionTitle}>
                Medical Conditions
              </h2>
              <div className={styles.tableCard}>
                {medicalDataForPatient && medicalDataForPatient.medicalConditions && medicalDataForPatient.medicalConditions.length > 0 ? (
                  <table className={styles.medicalTable}>
                    <thead>
                      <tr>
                        <th className={styles.tableHeaderCell}>CONDITION</th>
                        <th className={styles.tableHeaderCell}>DIAGNOSED DATE</th>
                        <th className={styles.tableHeaderCell}>STATUS</th>
                        <th className={styles.tableHeaderCell}>NOTES</th>
                      </tr>
                    </thead>
                    <tbody>
                      {medicalDataForPatient.medicalConditions.map((condition, index) => (
                        <tr key={index} className={styles.tableRow}>
                          <td className={styles.tableCell}>{condition.condition}</td>
                          <td className={styles.tableCell}>{condition.diagnosedDate}</td>
                          <td className={styles.tableCell}>
                            <span className={condition.status === 'Active' ? styles.statusActive : styles.statusResolved}>
                              {condition.status}
                            </span>
                          </td>
                          <td className={styles.tableCell}>{condition.notes}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className={styles.emptyState}>
                    <div>No medical conditions data found.</div>
                  </div>
                )}
              </div>
            </div>

            {/* Medications Section */}
            <div className={styles.medicalSection}>
              <h2 className={styles.sectionTitle}>
                Medications
              </h2>
              <div className={styles.tableCard}>
                {medicalDataForPatient && medicalDataForPatient.medications && medicalDataForPatient.medications.length > 0 ? (
                  <table className={styles.medicalTable}>
                    <thead>
                      <tr>
                        <th className={styles.tableHeaderCell}>MEDICATION</th>
                        <th className={styles.tableHeaderCell}>DOSAGE</th>
                        <th className={styles.tableHeaderCell}>FREQUENCY</th>
                        <th className={styles.tableHeaderCell}>START DATE</th>
                      </tr>
                    </thead>
                    <tbody>
                      {medicalDataForPatient.medications.map((medication, index) => (
                        <tr key={index} className={styles.tableRow}>
                          <td className={styles.tableCell}>{medication.medication}</td>
                          <td className={styles.tableCell}>{medication.dosage}</td>
                          <td className={styles.tableCell}>{medication.frequency}</td>
                          <td className={styles.tableCell}>{medication.startDate}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className={styles.emptyState}>
                    <div>No medications data found.</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      case 'visits': {
        // Debug log
        console.log('nurseCalendar:', nurseCalendar);
        console.log('Current patient id:', patient.id);
        nurseCalendar.forEach((day: CalendarDay) => {
          (day.slots || []).forEach((slot: CalendarSlot) => {
            if (slot.status === 'busy') {
              console.log('Busy slot:', slot, 'Slot patient id:', slot.patient?.id, typeof slot.patient?.id);
            }
          });
        });
        // Find all busy slots for this patient
        const bookings: { day: string; slot: CalendarSlot }[] = [];
        nurseCalendar.forEach((day: CalendarDay) => {
          (day.slots || []).forEach((slot: CalendarSlot) => {
            if (slot.status === 'busy' && slot.patient) {
              console.log('Comparing:', slot.patient.id, typeof slot.patient.id, 'with', patient.id, typeof patient.id);
              if (slot.patient.id == patient.id) {
                bookings.push({ day: day.day, slot });
              }
            }
          });
        });
        if (bookings.length === 0) {
          return (
            <div className={styles.tabContent + ' ' + styles.emptyState}>
              <svg className={styles.emptyStateIcon} width="48" height="48" viewBox="0 0 48 48" fill="none">
                <rect x="8" y="10" width="32" height="28" rx="6" fill="#e3f2fd"/>
                <rect x="8" y="16" width="32" height="22" rx="0" fill="#fff"/>
                <rect x="8" y="10" width="32" height="28" rx="6" stroke="#1976d2" strokeWidth="2"/>
                <rect x="14" y="6" width="4" height="8" rx="2" fill="#1976d2"/>
                <rect x="30" y="6" width="4" height="8" rx="2" fill="#1976d2"/>
                <rect x="14" y="20" width="4" height="4" rx="1.2" fill="#90caf9"/>
                <rect x="22" y="20" width="4" height="4" rx="1.2" fill="#90caf9"/>
                <rect x="30" y="20" width="4" height="4" rx="1.2" fill="#90caf9"/>
                <rect x="14" y="28" width="4" height="4" rx="1.2" fill="#1976d2"/>
                <rect x="22" y="28" width="4" height="4" rx="1.2" fill="#1976d2"/>
                <rect x="30" y="28" width="4" height="4" rx="1.2" fill="#1976d2"/>
              </svg>
              <div>No appointments booked.</div>
            </div>
          );
        }
        return (
          <div className={styles.tabContent}>
            <div className={styles.cardGrid}>
              {bookings.map(({ day, slot }) => {
                const start = new Date(slot.startTime);
                const duration = slot.slot || 30;
                return (
                  <div key={slot.scheduleId} className={styles.workflowCard}>
                    <div className={styles.cardIconRow}>
                      <svg width="22" height="22" fill="#1976d2" viewBox="0 0 24 24" style={{ marginRight: 10 }}><path d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V9h14v11zm0-13H5V6h14v1z"/></svg>
                      <span className={styles.cardFileName}>
                        {start.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                      </span>
                    </div>
                    <div className={styles.cardMeta}>
                      {start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} <span className={styles.cardSubtitle}>({duration} min)</span>
                    </div>
                    <div className={styles.cardStatus}>
                      <span className={styles.status}>Upcoming</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      }
      case 'documents':
        if (loadingDocs) {
          return <div className={styles.tabContent}>Loading documents...</div>;
        }
        if (docError) {
          return <div className={styles.tabContent}>{docError}</div>;
        }
        if (!documents || documents.length === 0) {
          return (
            <div className={styles.tabContent + ' ' + styles.emptyState}>
              <svg className={styles.emptyStateIcon} width="48" height="48" viewBox="0 0 48 48" fill="none">
                <rect x="6" y="8" width="36" height="32" rx="6" fill="#e3f2fd"/>
                <rect x="14" y="4" width="20" height="8" rx="2" fill="#90caf9"/>
                <path d="M14 20h20M14 28h12" stroke="#1976d2" strokeWidth="2.2" strokeLinecap="round"/>
                <rect x="6" y="8" width="36" height="32" rx="6" stroke="#1976d2" strokeWidth="2"/>
              </svg>
              <div>No documents found.</div>
            </div>
          );
        }
        return (
          <div className={styles.tabContent}>
            <div className={styles.cardGrid}>
              {documents.map(doc => {
                const isPdf = doc.fileName.toLowerCase().endsWith('.pdf');
                const isImage = /\.(jpg|jpeg|png|gif)$/i.test(doc.fileName);
                return (
                  <div key={doc.fileName} className={styles.workflowCard + (isPdf ? ' ' + styles.documentCard : '')}>
                    <div className={styles.cardIconRow}>
                      {isPdf ? (
                        <AdobePdfIcon style={{ marginRight: 10, borderRadius: 6, boxShadow: '0 1px 4px rgba(226,35,26,0.10)', background: '#fff' }} width={22} height={22} />
                      ) : isImage ? (
                        <svg width="22" height="22" fill="#1976d2" viewBox="0 0 24 24" style={{ marginRight: 10 }}><path d="M21 19V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2zm-2 0H5V5h14zm-7-3l2.03 2.71L17 14l4 5H3l5-6.5z"/></svg>
                      ) : (
                        <svg width="22" height="22" fill="#888" viewBox="0 0 24 24" style={{ marginRight: 10 }}><circle cx="12" cy="12" r="10" /></svg>
                      )}
                      <span className={styles.cardFileName}>{doc.fileName}</span>
                    </div>
                    <div className={styles.cardMeta}>{getFileExtension(doc.fileName).toUpperCase()} file</div>
                    <button
                      className={styles.viewButton}
                      onClick={() => {
                        if (isPdf) {
                          window.open(doc.url, '_blank', 'noopener,noreferrer');
                        } else {
                          setPreviewUrl(doc.url);
                        }
                      }}
                    >
                      View
                    </button>
                  </div>
                );
              })}
            </div>
            {/* Preview Modal */}
            {previewUrl && (
              <>
                <div className={styles.modalBackdrop} onClick={() => setPreviewUrl(null)} />
                <dialog ref={previewRef} open className={styles.modalDialog}>
                  <button className={styles.modalCloseButton} onClick={() => setPreviewUrl(null)} aria-label="Close preview">×</button>
                  <div className={styles.modalContent}>
                    <Zoom>
                      <img className={styles.modalImage} src={previewUrl} alt="Preview" />
                    </Zoom>
                  </div>
                </dialog>
              </>
            )}
          </div>
        );
      case 'physicianFeedback': {
        // Find the comprehensive report step
        const comprehensiveStep = workflow.find(
          step => step.title && step.title.toLowerCase().includes('comprehensive report')
        );
        const nurseId = getNurseId();
        const token = getAccessToken();
        const feedbackExists = comprehensiveStep && comprehensiveStep.actionData && typeof comprehensiveStep.actionData.feedback === 'string';
        const canSubmit = feedback.trim() && !submittingFeedback && comprehensiveStep && nurseId && token && (editingFeedback || !feedbackExists);
        return (
          <div className={styles.tabContent}>
            {/* AI Generated Summary Section */}
            <div className={styles.summarySection}>
              <h2 className={styles.sectionTitle}>
                AI Generated Summary
              </h2>
              <div className={styles.summaryCard}>
                <div className={styles.summaryAccentBar}></div>
                <div className={styles.summaryCardHeader}>
                  <h4 className={styles.summaryCardTitle}>Patient: {patient.demographics.fullName || patient.demographics.nickname || 'Unknown'}</h4>
                  <span className={styles.summaryLastUpdated}>
                    Last generated: {reportStep?.updatedAt ? formatDate(reportStep.updatedAt) : '15 minutes ago'}
                  </span>
                </div>
                
                {fetchStep && fetchStep.stepStatus === 'completed' ? (
                  <>
                    <p className={styles.summaryText}>
                      {aiSummaryDataForPatient.summary.replace('{patientName}', patient.demographics.fullName || patient.demographics.nickname || 'John Doe')}
                    </p>
                    <h5 className={styles.summarySubtitle}>Key Insights:</h5>
                    <ul className={styles.summaryList}>
                      {aiSummaryDataForPatient.keyInsights.map((insight, index) => (
                        <li key={index}>{insight}</li>
                      ))}
                    </ul>
                    <h5 className={styles.summarySubtitle}>Potential Anomalies & Trends:</h5>
                    {aiSummaryDataForPatient.potentialAnomalies.length > 0 && (
                      <div className={styles.summaryWarning}>
                        <ReportProblemOutlinedIcon style={{ color: '#f7971e', marginRight: 8 }} />
                        {aiSummaryDataForPatient.potentialAnomalies.map((anomaly: any) => anomaly.message).join('. ')}
                      </div>
                    )}
                  </>
                ) : (
                  <div className={styles.summaryPlaceholder}>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      padding: '40px 20px',
                      color: '#666',
                      fontSize: '16px',
                      textAlign: 'center'
                    }}>
                      <div>
                        <div style={{ marginBottom: '12px' }}>
                          {docScannerData ? (
                            <Lottie 
                              animationData={docScannerData} 
                              loop={true} 
                              style={{ width: '120px', height: '120px', margin: '0 auto' }}
                              onError={(error) => {
                                console.error('Lottie animation error:', error);
                                setDocScannerData(null);
                              }}
                            />
                          ) : (
                            <div style={{ 
                              width: '120px', 
                              height: '120px', 
                              margin: '0 auto',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              <svg 
                                width="48" 
                                height="48" 
                                viewBox="0 0 24 24" 
                                fill="none" 
                                stroke="currentColor" 
                                strokeWidth="1.5" 
                                style={{ 
                                  color: '#ccc',
                                  animation: 'rotateMagnifier 2s linear infinite'
                                }}
                              >
                                <circle cx="11" cy="11" r="8"></circle>
                                <path d="m21 21-4.35-4.35"></path>
                              </svg>
                            </div>
                          )}
                        </div>
                        <div style={{ fontSize: '14px', lineHeight: '1.4' }}>
                          AI summary will be generated once medical records are fetched and analyzed.
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Physician Notes Section */}
            <div className={styles.notesSection}>
              <h2 className={styles.sectionTitle}>Physician Notes</h2>
              {feedbackExists && !editingFeedback ? (
                <div className={styles.noteCard}>
                  <div className={styles.noteCardHeader}>
                    <h4 className={styles.noteCardTitle}>Note for {patient.demographics.fullName || patient.demographics.nickname || 'John Doe'} - Follow-up Plan</h4>
                    <span className={styles.noteLastSaved}>Last saved: {formatDate(comprehensiveStep?.updatedAt)}
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: 8, marginRight: 4, cursor: 'pointer' }} onClick={() => setEditingFeedback(true)}><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>
                    </span>
                  </div>
                  <p className={styles.noteText}>{feedback}</p>
                </div>
              ) : (
                <div className={styles.addEditNoteCard}>
                  <h4 className={styles.addEditNoteTitle}>Add / Edit Note</h4>
                  <textarea
                    className={styles.noteTextarea}
                    value={feedback}
                    onChange={e => {
                      setFeedback(e.target.value);
                      setFeedbackError(null);
                    }}
                    placeholder="Start typing your notes here..."
                    disabled={submittingFeedback}
                  />
                  <button
                    className={styles.saveNoteButton}
                    onClick={handleSave}
                    disabled={submittingFeedback || !feedback.trim()}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 6 }}><polyline points="20 6 9 17 4 12"></polyline></svg>
                    Save Note
                  </button>
                </div>
              )}
            </div>
          </div>
        );
      }
      case 'other':
        return <div className={styles.tabContent}>Other content goes here.</div>;
      default:
        return null;
    }
  };

  return (
    <div style={{ background: '#f5f7fa', padding: '0', minHeight: '100vh' }}>
      {/* Patient Demographics Card */}
      <div className={styles.demographicsCardMargin}>
        <div className={styles.card}>
          {/* <div className={styles.accentBar} /> */}
          {/* Row: Profile Pic + Name */}
          <div className={styles.profileRow}>
            <div className={styles.profilePic}>
              {patient.demographics?.pictureUrl ? (
                <img src={patient.demographics.pictureUrl} alt={patient.demographics.fullName || 'Patient'} className={styles.profileImg} />
              ) : (
                patient.demographics?.firstName?.[0] || '?'
              )}
              <span className={styles.profileDot} />
            </div>
            <div className={styles.profileName}>
              {patient.demographics?.fullName || patient.demographics?.nickname || 'Unknown'}
            </div>
          </div>
          {/* Details as 2-column clean layout */}
          <div className={styles.detailsGrid}>
            {/* Left column */}
            <div className={styles.detailsCol}>
              {patient.demographics?.email && (
                <div className={styles.detailsField}>
                  <div className={styles.detailsLabel}>Email</div>
                  <div 
                    className={`${styles.detailsValue} ${styles.emailValue} ${emailRevealed ? styles.revealed : ''}`}
                    onClick={() => setEmailRevealed(!emailRevealed)}
                    title={emailRevealed ? 'Click to hide email' : 'Click to reveal email'}
                  >
                    <span className={styles.emailText}>
                      {emailRevealed ? patient.demographics.email : maskEmail(patient.demographics.email)}
                    </span>
                    <svg 
                      width="14" 
                      height="14" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                      className={`${styles.emailIcon} ${emailRevealed ? styles.revealed : ''}`}
                    >
                      {emailRevealed ? (
                        <>
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                          <circle cx="12" cy="12" r="3"></circle>
                        </>
                      ) : (
                        <>
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                          <line x1="1" y1="1" x2="23" y2="23"></line>
                        </>
                      )}
                    </svg>
                  </div>
                </div>
              )}
              {patient.demographics?.age && (
                <div className={styles.detailsField}>
                  <div className={styles.detailsLabel}>Age</div>
                  <div className={styles.detailsValue}>{patient.demographics.age}</div>
                </div>
              )}
              {patient.demographics?.gender && (
                <div className={styles.detailsField}>
                  <div className={styles.detailsLabel}>Gender</div>
                  <div className={styles.detailsValue}>{patient.demographics.gender}</div>
                </div>
              )}
              {patient.patientLastActiveTime && (
                  <div className={styles.detailsField}>
                    <div className={styles.detailsLabel}>Last Accessed</div>
                    <div className={styles.detailsValue}>{new Date(patient.patientLastActiveTime).toLocaleString()}</div>
                  </div>
              )}
            </div>
            {/* Right column */}
            <div className={styles.detailsCol}>
              {patient.demographics?.firstName && (
                <div className={styles.detailsField}>
                  <div className={styles.detailsLabel}>First Name</div>
                  <div className={styles.detailsValue}>{patient.demographics.firstName}</div>
                </div>
              )}
              {patient.demographics?.lastName && (
                <div className={styles.detailsField}>
                  <div className={styles.detailsLabel}>Last Name</div>
                  <div className={styles.detailsValue}>{patient.demographics.lastName}</div>
                </div>
              )}

            </div>
          </div>
        </div>
      </div>
      {/* Horizontal Stepper */}
      {renderStepper()}
      {/* Tab View */}
      {renderTabs()}
      {/* Tab Content */}
      {renderTabContent()}
      {/* Fade-in animation keyframes */}
      <style>{`
        @keyframes fadeInCard {
          from { opacity: 0; transform: translateY(24px) scale(0.98); }
          to { opacity: 1; transform: none; }
        }
        
        @keyframes rotateMagnifier {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default PatientWorkflowPage; 